import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/shared/Avatar";
import { NewOfferDialog } from "@/components/offer/NewOfferDialog";
import BookMeetingFromRecord from "@/components/salgsmuligheter/BookMeetingFromRecord";
import NewOpportunityDialog from "@/components/salgsmuligheter/NewOpportunityDialog";
import { useCanBookMeetings } from "@/hooks/use-can-book-meetings";
import { useCustomerOpportunities, type OpportunityWithRelations } from "@/hooks/use-opportunities";
import { daysInStage, isOpenStage, openProcessesWithoutOffer, STAGE_LABELS } from "@/lib/opportunity-calc";

interface CustomerOpportunitiesSectionProps {
  customerId: string;
  customerName: string;
  /**
   * Innebygd i «Tilbud ute»: bare listen under underoverskriften «Prosesser
   * uten tilbud», og ingenting når kunden ikke har slike prosesser. «Ny
   * prosess» og dialogen eies da av «Tilbud ute».
   */
  embedded?: boolean;
}

/** «Prosesser» på kundekortet: åpne salgsmuligheter med fase, eier og dager i fasen. */
export default function CustomerOpportunitiesSection({ customerId, customerName, embedded = false }: CustomerOpportunitiesSectionProps) {
  const { data: opportunities = [], isLoading } = useCustomerOpportunities(customerId);
  const [newOpen, setNewOpen] = useState(false);
  const [offerFor, setOfferFor] = useState<OpportunityWithRelations | null>(null);
  const [bookFor, setBookFor] = useState<OpportunityWithRelations | null>(null);
  const canBook = useCanBookMeetings();
  const open = embedded ? openProcessesWithoutOffer(opportunities) : opportunities.filter((o) => isOpenStage(o.stage));
  const now = new Date();

  if (embedded && open.length === 0) return null;

  const list = (
    <ul className={embedded ? "divide-y divide-border" : "divide-y divide-border border-[1.5px] border-foreground bg-card"}>
      {open.map((o) => {
        const days = daysInStage(o.stage_entered_at, now);
        return (
          <li key={o.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 p-3 text-sm">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{o.title}</span>
              <span className="block text-xs text-muted-foreground">
                {STAGE_LABELS[o.stage]} · {days === 1 ? "1 dag" : `${days} dager`} i fasen
              </span>
            </span>
            <Avatar name={o.owner?.full_name} avatarUrl={o.owner?.avatar_url} size="sm" />
            {canBook && (
              <Button variant="outline" size="sm" onClick={() => setBookFor(o)}>
                Book møte
              </Button>
            )}
            {o.offer_id ? (
              <Button asChild variant="outline" size="sm">
                <Link to={`/tilbud/${o.offer_id}`}>Åpne tilbud</Link>
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setOfferFor(o)}>
                Lag tilbud
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );

  const dialogs = (
    <>
      {bookFor && (
        <BookMeetingFromRecord
          open
          onOpenChange={(o) => !o && setBookFor(null)}
          customerId={customerId}
          name={customerName}
          opportunityId={bookFor.id}
          topic={bookFor.title}
        />
      )}
      {offerFor && (
        <NewOfferDialog
          open
          onOpenChange={(o) => !o && setOfferFor(null)}
          initialCustomerId={customerId}
          initialTitle={offerFor.title}
          opportunityId={offerFor.id}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-1 border-t border-border px-5 py-4">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Prosesser uten tilbud</h3>
        {list}
        {dialogs}
      </div>
    );
  }

  return (
    <section aria-label="Prosesser" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-xl font-semibold">Prosesser</h2>
        <Button variant="outline" size="sm" onClick={() => setNewOpen(true)}>
          Ny prosess
        </Button>
      </div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Laster ...</p>
      ) : open.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ingen åpne prosesser.</p>
      ) : (
        list
      )}
      <NewOpportunityDialog open={newOpen} onOpenChange={setNewOpen} customerId={customerId} customerName={customerName} />
      {dialogs}
    </section>
  );
}
